import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { indexStore } from "../../store/indexStore";
import { IMG_URl } from "../../common/img_url";
import { useTranslation } from "react-i18next";
import Iterator from "../utils/Iterator";
import { Skeleton } from "../ui/skeleton";
import Visibility from "../utils/Visibility";
import SearchField from "../Searchfield/ SearchField";
import { getRecentlyViewed } from "../utils/RecentlyViewed";
import RecentlyViewed from "./Recently/RecentlyViewed";
import MenuModel from "../../Models/MenuModel";
import BranchModel from "../../Models/BranchModel";
import ProductModel from "../../Models/PorductModel";
import { CiLocationOn } from "react-icons/ci";
import { MdOutlinePhoneInTalk } from "react-icons/md";
import { Link } from "react-router-dom";
import { DarkModeTogle } from "../langSelect/DarkModeTogle";

function ResponsiveBranchScreen() {
  const { menus, branch, loading, name, } = indexStore();
  const navigate = useNavigate();
  const onClickMenu = (id: number | string) => {
    navigate(`/branches/${params.branch_id}/${id}`);
  };
  const onRecentlyClick = (item: ProductModel) => {
    navigate(`/branches/${params.branch_id}/${item.category?.section_id}/${item.id}`);
  };
  const params = useParams();
  const recentlyViewed = getRecentlyViewed(Number(params.branch_id));

  const { t } = useTranslation();

  // Get branch address based on language
  const addressKey = name === "name_en" ? "address_en" : "address_ar";
  const address = branch?.[addressKey as keyof BranchModel];
  const branchName = branch?.[name as keyof BranchModel];

  // صورة الفرع (إن توفرت) مع fallback احتياطي
  const branchImage = branch?.image;

  // رابط الخريطة المفضل: address_link ثم الإحداثيات ثم العنوان النصي
  const resolvedMapUrl =
    branch?.address_link ||
    (branch?.latitude &&
      branch?.longitude &&
      `https://www.google.com/maps/search/?api=1&query=${branch.latitude},${branch.longitude}`) ||
    (address &&
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        address as string
      )}`);

  return (
    <>
      <Visibility visible={!loading}>
        <div className="min-h-screen bg-primary flex flex-col">

          {/* Branch Information Card */}
          <div className="container mx-auto px-4 py-6">
            <div className="bg-primary rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-2xl mb-6 relative overflow-hidden">
              {/* Decorative background pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-32 h-32 bg-onPrimary rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 right-0 w-40 h-40 bg-onPrimary rounded-full translate-x-1/2 translate-y-1/2"></div>
              </div>

              <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
                {/* Branch Logo / Image */}
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-onPrimary/10 p-2 flex items-center justify-center shadow-lg">
                    {branchImage && (
                      <img
                        className="w-full h-full object-cover rounded-full"
                        src={`${IMG_URl}${branchImage}`}
                        alt={String(branchName || "")}
                      />
                    )}
                  </div>
                </div>

                {/* Branch Details */}
                <div className="flex-1 flex flex-col gap-3 md:gap-4 text-onPrimary">
                  {/* Branch Name */}
                  {branchName && (
                    <h2 className="text-2xl md:text-3xl font-bold">
                      {branchName}
                    </h2>
                  )}

                  {/* Phone Number */}
                  {branch?.phone && (
                    <Link
                      to={`tel:${branch.phone}`}
                      className="flex items-center gap-2 text-lg md:text-xl hover:opacity-80 transition-opacity"
                    >
                      <MdOutlinePhoneInTalk className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" />
                      <span dir="ltr">{branch.phone}</span>
                    </Link>
                  )}

                  {/* Address with Map Icon */}
                  {address && (
                    <div className="flex items-start gap-2">
                      <CiLocationOn className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0 mt-1" />
                      <p className="text-base md:text-lg flex-1 leading-relaxed">
                        {address}
                      </p>
                      {/* Map Link Button */}
                      {resolvedMapUrl && (
                        <button
                          className="bg-onPrimary/20 hover:bg-onPrimary/30 rounded-lg p-2 transition-colors flex-shrink-0"
                          aria-label="View on map"
                          onClick={() => window.open(resolvedMapUrl, "_blank")}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Search Bar */}
            {/* <div className="flex gap-2 md:gap-3 items-center">
              <div className="flex-1 opacity-0 animate-[fadeIn_0.3s_ease-out_0.1s_forwards]">
                <SearchField />
              </div>
              <button className="bg-background rounded-full p-2.5 md:p-3 shadow-md hover:shadow-lg hover:bg-muted transition-all duration-300 opacity-0 animate-[fadeIn_0.3s_ease-out_0.2s_forwards]">
                <DarkModeTogle showText={false} />
              </button>
            </div> */}
          </div>

          {/* Menu Items Grid - White Background Section */}
          <div className="flex-1 bg-background rounded-t-[2rem] md:rounded-t-[3rem] pt-6 md:pt-8 pb-24 md:pb-32">
            <div className="container mx-auto px-4">
              <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6">
                {t("gloabl.menus")}
              </h2>

              {/* Responsive Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4 mb-8">
                <Iterator
                  data={menus}
                  render={(item: MenuModel, i) => (
                    <div
                      key={item.id || i}
                      className="group cursor-pointer opacity-0 animate-fadeInUp transition-all duration-300 hover:scale-105"
                      style={{
                        animationDelay: `${Math.min(i * 50, 500)}ms`,
                      }}
                      onClick={() => onClickMenu(item.id)}
                    >
                      <div className="bg-background rounded-xl md:rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border-2 border-primary/20 group-hover:border-primary">
                        {/* Menu Image */}
                        <div className="aspect-[5/3] md:aspect-[3/2] overflow-hidden bg-muted">
                          <img
                            src={`${IMG_URl}${item.image}`}
                            alt={item[name as keyof MenuModel] as string}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>

                        {/* Menu Name */}
                        <div className="p-3 md:p-4 text-center">
                          <p className="text-sm md:text-base font-bold text-primary line-clamp-2">
                            {item[name as keyof MenuModel]}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                />
              </div>

              {/* Recently Viewed Section */}
              {recentlyViewed.length > 0 && (
                <div className="mt-8 md:mt-12">
                  <h2 className="text-xl md:text-2xl font-bold text-primary mb-4 md:mb-6">
                    {t("gloabl.recently")}
                  </h2>
                  <RecentlyViewed
                    recentlyViewed={recentlyViewed}
                    onRecentlyClick={onRecentlyClick}
                    IMG_URl={IMG_URl}
                    name={name as keyof ProductModel}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Visibility>

      <Visibility visible={loading}>
        <RenderLoading />
      </Visibility>
    </>
  );
}

export default ResponsiveBranchScreen;

// Loading Skeleton Component
const RenderLoading = () => {
  return (
    <div className="min-h-screen bg-primary flex flex-col">

      {/* Branch Card Skeleton */}
      <div className="container mx-auto px-4 py-6">
        <div className="bg-primary rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-2xl mb-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
            <Skeleton className="w-24 h-24 md:w-32 md:h-32 rounded-full" />
            <div className="flex-1 flex flex-col gap-3 w-full">
              <Skeleton className="w-48 h-8" />
              <Skeleton className="w-40 h-6" />
              <Skeleton className="w-full h-6" />
            </div>
          </div>
        </div>

      </div>

      {/* Menu Grid Skeleton */}
      <div className="flex-1 bg-background rounded-t-[2rem] md:rounded-t-[3rem] pt-6 md:pt-8 pb-24">
        <div className="container mx-auto px-4">
          <Skeleton className="w-32 h-8 mb-6" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i}>
                <Skeleton className="w-full aspect-square rounded-xl md:rounded-2xl mb-3" />
                <Skeleton className="w-full h-6 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
